// ProjectContext.js - 프로젝트 중심 상태 관리 컨텍스트
// 프로젝트 중심 아키텍처 구현

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import API from '../config/api';

const ProjectContext = createContext();

export const useProject = () => {
  const context = useContext(ProjectContext);
  if (!context) {
    throw new Error('useProject must be used within a ProjectProvider');
  }
  return context;
};

export const ProjectProvider = ({ children }) => {
  const queryClient = useQueryClient();
  
  // 현재 프로젝트 ID (localStorage에서 비동기 복원 - 초기 렌더링 블로킹 방지)
  const [currentProjectId, setCurrentProjectId] = useState(null);
  const [isInitialized, setIsInitialized] = useState(false);

  // localStorage에서 프로젝트 ID 복원 (비동기)
  useEffect(() => {
    const projectId = localStorage.getItem('currentProjectId');
    setCurrentProjectId(projectId || null);
    setIsInitialized(true);
  }, []);

  // 현재 프로젝트 정보 조회 (React Query) - 프로젝트 ID가 있고 초기화가 완료된 경우에만 조회
  const { data: currentProject, isLoading: projectLoading, error: projectError } = useQuery({
    queryKey: ['projects', currentProjectId],
    queryFn: async () => {
      if (!currentProjectId) return null;
      try {
        const response = await axios.get(API.PROJECTS.DETAIL(currentProjectId));
        return response.data;
      } catch (error) {
        console.error('프로젝트 정보 조회 실패:', error);
        if (error?.response?.status === 404) {
          console.warn(`프로젝트 ${currentProjectId}를 찾을 수 없습니다. 선택 상태를 초기화합니다.`);
          setCurrentProjectId(null);
          localStorage.removeItem('currentProjectId');
        }
        return null;
      }
    },
    enabled: !!currentProjectId && isInitialized, // 초기화 완료 후에만 활성화
    staleTime: 1000 * 60 * 5, // 5분간 캐시 유지
    gcTime: 1000 * 60 * 30, // 30분간 캐시 보관 (v5에서 cacheTime -> gcTime으로 변경)
  });

  // 프로젝트 변경
  const changeProject = useCallback((projectId) => {
    setCurrentProjectId(projectId);
    if (projectId) {
      localStorage.setItem('currentProjectId', projectId);
    } else {
      localStorage.removeItem('currentProjectId');
    }
    // 프로젝트 변경 시 관련 쿼리 캐시 무효화
    queryClient.invalidateQueries(['projects', projectId]);
  }, [queryClient]);

  // 프로젝트 목록 조회 (React Query) - 지연 로딩: 필요할 때만 활성화
  const [shouldLoadProjects, setShouldLoadProjects] = useState(false);
  const { data: projects = [], isLoading: projectsLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: async () => {
      try {
        const response = await axios.get(API.PROJECTS.LIST, {
          params: { user_id: 'default_user', status: 'active' }
        });
        return response.data;
      } catch (error) {
        console.error('프로젝트 목록 조회 실패:', error);
        return [];
      }
    },
    enabled: shouldLoadProjects, // 명시적으로 활성화될 때만 조회
    staleTime: 1000 * 60 * 5,
    gcTime: 1000 * 60 * 30, // v5에서 cacheTime -> gcTime으로 변경
  });

  // 프로젝트 목록을 로드하는 함수 (필요할 때 호출)
  const loadProjects = useCallback(() => {
    setShouldLoadProjects(true);
  }, []);

  // 프로젝트 목록이 자동으로 필요한 경우 (예: 프로젝트 선택 UI가 표시될 때)
  useEffect(() => {
    // TopBar가 마운트되면 프로젝트 목록 로드 (약간의 지연)
    const timer = setTimeout(() => {
      setShouldLoadProjects(true);
    }, 100); // 100ms 지연으로 초기 렌더링 블로킹 방지

    return () => clearTimeout(timer);
  }, []);

  // 프로젝트 생성
  const createProject = useCallback(async (projectData) => {
    try {
      const response = await axios.post(API.PROJECTS.CREATE, projectData, {
        params: { user_id: 'default_user' }
      });
      // 프로젝트 목록 캐시 무효화
      queryClient.invalidateQueries(['projects']);
      // 생성된 프로젝트로 전환
      changeProject(response.data.project_id);
      return response.data;
    } catch (error) {
      console.error('프로젝트 생성 실패:', error);
      throw error;
    }
  }, [queryClient, changeProject]);

  // 프로젝트 삭제
  const deleteProject = useCallback(async (projectId) => {
    try {
      await axios.delete(API.PROJECTS.DELETE(projectId));
      // 프로젝트 목록 캐시 무효화
      queryClient.invalidateQueries(['projects']);
      // 현재 프로젝트가 삭제된 경우 선택 해제
      if (currentProjectId === projectId) {
        changeProject(null);
      }
    } catch (error) {
      console.error('프로젝트 삭제 실패:', error);
      throw error;
    }
  }, [queryClient, currentProjectId, changeProject]);

  return (
    <ProjectContext.Provider
      value={{
        currentProjectId,
        currentProject,
        projects,
        changeProject,
        createProject,
        deleteProject,
        loadProjects, // 프로젝트 목록 수동 로드 함수 추가
        isLoading: projectLoading || projectsLoading || !isInitialized,
        error: projectError,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};

